import { useState, useEffect } from 'react';
import { Outlet, Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import LanguageSwitcher from '@/components/feature/LanguageSwitcher';
import { signOut } from '@/lib/auth';

interface NavItem {
  key: string;
  path: string;
  icon: string;
  badge?: string;
}

const NAV_KEYS: NavItem[] = [
  { key: 'admin.nav_dashboard', path: '/admin/dashboard', icon: 'ri-dashboard-line' },
  { key: 'admin.nav_pages', path: '/admin/pages-content', icon: 'ri-file-text-line' },
  { key: 'admin.nav_treatments', path: '/admin/treatments', icon: 'ri-price-tag-3-line' },
  { key: 'admin.nav_team', path: '/admin/team-manage', icon: 'ri-team-line' },
  { key: 'admin.nav_reviews', path: '/admin/reviews', icon: 'ri-chat-quote-line' },
  { key: 'admin.nav_bookings', path: '/admin/bookings', icon: 'ri-calendar-check-line' },
  { key: 'admin.nav_newsletter', path: '/admin/newsletter', icon: 'ri-mail-send-line' },
  { key: 'admin.nav_media', path: '/admin/media', icon: 'ri-image-edit-line' },
  { key: 'admin.nav_translations', path: '/admin/translations', icon: 'ri-translate-2' },
  { key: 'admin.nav_footer', path: '/admin/footer-contact', icon: 'ri-contacts-line' },
  { key: 'admin.nav_seo', path: '/admin/seo', icon: 'ri-search-eye-line' },
  { key: 'admin.nav_settings', path: '/admin/settings', icon: 'ri-settings-3-line' },
];

export default function AdminLayout() {
  const { t } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);

  // Close the mobile drawer whenever the route changes
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const isActive = (path: string) => {
    if (path === '/admin/dashboard') return location.pathname === '/admin/dashboard';
    return location.pathname.startsWith(path);
  };

  const handleLogout = async () => {
    await signOut();
    navigate('/admin');
  };

  const getBreadcrumb = () => {
    const current = NAV_KEYS.find((item) => {
      if (item.path === '/admin/dashboard') return location.pathname === '/admin/dashboard';
      return location.pathname.startsWith(item.path);
    });
    return current ? t(current.key) : t('admin.breadcrumb_admin');
  };

  return (
    <div className="min-h-screen bg-background-100 flex">
      {/* ── Mobile overlay ─────────────────────────── */}
      {mobileOpen && (
        <div
          className="fixed inset-0 z-30 bg-black/50 md:hidden"
          onClick={() => setMobileOpen(false)}
        />
      )}

      {/* ── Sidebar (off-canvas drawer on mobile) ──── */}
      <aside
        className={`bg-foreground-950 flex flex-col flex-shrink-0 transition-all duration-200 border-r border-foreground-900
          fixed inset-y-0 left-0 z-40 w-[240px]
          ${mobileOpen ? 'translate-x-0' : '-translate-x-full'}
          md:static md:translate-x-0 md:z-auto
          ${sidebarCollapsed ? 'md:w-[72px]' : 'md:w-[240px]'}`}
      >
        {/* Logo */}
        <div className="h-16 flex items-center px-5 border-b border-foreground-900">
          {!sidebarCollapsed && (
            <div className="flex items-center gap-2.5 overflow-hidden">
              <div className="w-8 h-8 flex items-center justify-center rounded-lg bg-primary-500">
                <i className="ri-shield-keyhole-line text-white text-sm"></i>
              </div>
              <span className="font-heading text-sm font-bold text-white tracking-[0.08em] whitespace-nowrap">
                {t('admin.logo')}
              </span>
            </div>
          )}
          {sidebarCollapsed && (
            <div className="w-8 h-8 mx-auto flex items-center justify-center rounded-lg bg-primary-500">
              <i className="ri-shield-keyhole-line text-white text-sm"></i>
            </div>
          )}
        </div>

        {/* Nav Items */}
        <nav className="flex-1 px-3 py-4 space-y-0.5 overflow-y-auto">
          {NAV_KEYS.map((item) => {
            const active = isActive(item.path);
            return (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-3 py-2.5 rounded-lg transition-all cursor-pointer no-underline group ${
                  active
                    ? 'bg-primary-500 text-white'
                    : 'text-foreground-400 hover:text-white hover:bg-foreground-900'
                } ${sidebarCollapsed ? 'justify-center px-0' : ''}`}
                title={sidebarCollapsed ? t(item.key) : undefined}
              >
                <div className={`w-5 h-5 flex items-center justify-center flex-shrink-0`}>
                  <i className={`${item.icon} text-sm`}></i>
                </div>
                {!sidebarCollapsed && (
                  <>
                    <span className="text-xs font-label whitespace-nowrap flex-1">{t(item.key)}</span>
                    {item.badge && (
                      <span className="px-1.5 py-0.5 rounded-full bg-accent-500 text-white text-[9px] font-label flex-shrink-0">
                        {item.badge}
                      </span>
                    )}
                  </>
                )}
              </Link>
            );
          })}
        </nav>

        {/* Bottom actions */}
        <div className="px-3 py-3 border-t border-foreground-900 space-y-1">
          <Link
            to="/"
            className={`flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-400 hover:text-white hover:bg-foreground-900 transition-all cursor-pointer no-underline ${
              sidebarCollapsed ? 'justify-center px-0' : ''
            }`}
            title={t('admin.to_website')}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-external-link-line text-sm"></i>
            </div>
            {!sidebarCollapsed && <span className="text-xs font-label whitespace-nowrap">{t('admin.to_website')}</span>}
          </Link>
          <button
            onClick={handleLogout}
            className={`w-full flex items-center gap-3 px-3 py-2.5 rounded-lg text-foreground-400 hover:text-red-400 hover:bg-foreground-900 transition-all cursor-pointer ${
              sidebarCollapsed ? 'justify-center px-0' : ''
            }`}
            title={t('admin.logout')}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className="ri-logout-box-line text-sm"></i>
            </div>
            {!sidebarCollapsed && <span className="text-xs font-label whitespace-nowrap">{t('admin.logout')}</span>}
          </button>
        </div>

        {/* Collapse toggle (desktop only; mobile uses the drawer) */}
        <div className="hidden md:block px-3 py-2 border-t border-foreground-900">
          <button
            onClick={() => setSidebarCollapsed(!sidebarCollapsed)}
            className={`w-full flex items-center gap-3 px-3 py-2 rounded-lg text-foreground-500 hover:text-white hover:bg-foreground-900 transition-all cursor-pointer ${
              sidebarCollapsed ? 'justify-center px-0' : ''
            }`}
            title={sidebarCollapsed ? t('admin.sidebar_expand') : t('admin.sidebar_collapse')}
          >
            <div className="w-5 h-5 flex items-center justify-center flex-shrink-0">
              <i className={`ri-${sidebarCollapsed ? 'sidebar-unfold' : 'sidebar-fold'}-line text-sm`}></i>
            </div>
          </button>
        </div>
      </aside>

      {/* ── Main Content Area ───────────────────────── */}
      <div className="flex-1 flex flex-col min-w-0">
        {/* Header */}
        <header className="h-16 bg-background-50 border-b border-background-200/70 flex items-center justify-between px-4 md:px-6 sticky top-0 z-20 flex-shrink-0">
          <div className="flex items-center gap-2 md:gap-3 min-w-0">
            <button
              onClick={() => setMobileOpen(true)}
              className="md:hidden w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-700 cursor-pointer flex-shrink-0"
              aria-label={t('shared.open_menu')}
            >
              <i className="ri-menu-line text-lg"></i>
            </button>
            <span className="hidden sm:inline text-xs text-foreground-400 font-label">{t('admin.breadcrumb_admin')}</span>
            <i className="hidden sm:inline ri-arrow-right-s-line text-foreground-300 text-xs"></i>
            <span className="text-sm font-label font-semibold text-foreground-900 truncate">{getBreadcrumb()}</span>
          </div>
          <div className="flex items-center gap-3">
            <LanguageSwitcher />
            <Link
              to="/admin/settings"
              className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 hover:text-foreground-600 transition-colors cursor-pointer no-underline"
              title={t('admin.header_settings')}
            >
              <i className="ri-settings-3-line text-sm"></i>
            </Link>
            <div className="w-7 h-7 flex items-center justify-center rounded-full bg-primary-100 text-primary-600">
              <i className="ri-user-3-line text-xs"></i>
            </div>
          </div>
        </header>

        {/* Page Content */}
        <main key={location.pathname} className="flex-1 overflow-y-auto">
          <Outlet />
        </main>
      </div>
    </div>
  );
}